/**
 * One resource group as a small multiple — the overview's unit of comparison.
 *
 * Levelled, the bar is ALWAYS full: clearing + due-this-week + free sum to exactly capacity,
 * so the information is in the mix and never in the height. As scheduled, the columns are
 * banded by ratio against a fixed capacity × 2.6 scale, so all six cards stay comparable and
 * a runaway week clips rather than crushing its neighbours.
 *
 * MARKUP NOTE: the card is a plain element with a click handler and the week columns inside
 * it are real buttons — a button inside a button is invalid HTML. Every action here is
 * reachable from the keyboard: the columns are focusable, and "Open resource →" in the
 * footer is a real button doing exactly what clicking the card does.
 */

import React from 'react'
import ColumnTooltip from './ColumnTooltip'
import type { ResourceModel, WeekModel } from '../../viewmodels/loading'
import { columnLabel, hrs, levelledSegs, scheduledSegs, tickEvery, weekLabel, weekShort } from './format'

const PLOT = 118
const MONO = 'font-mono text-[10.5px]'

function Badge({ r, levelled }: { r: ResourceModel; levelled: boolean }): React.JSX.Element {
  const clears = r.clearIdx >= 0
  const bad = levelled ? !clears : (r.peakPct ?? 0) > 100
  const text = levelled
    // THE WINDOW IS NAMED FROM THE SERVED WEEKS, never from a literal (31 Aug 2026). "never
    // clears in 13wk" under a 52-week horizon is a false statement about a true number.
    ? (clears ? `clears ${weekLabel(r.weeks[r.clearIdx].weekStart)}` : `never clears in ${r.weeks.length}wk`)
    : `peak ${r.peakPct == null ? '—' : `${Math.round(r.peakPct)}%`}`
  return (
    <span
      className="whitespace-nowrap rounded-[4px] px-1.5 py-[3px] font-mono text-[9px] font-semibold uppercase tracking-[.06em]"
      style={{
        background: bad ? 'var(--lc-red-bg)' : 'var(--lc-teal-bg)',
        color: bad ? 'var(--lc-red-ink)' : 'var(--lc-teal-ink)',
      }}
    >
      {text}
    </span>
  )
}

function Header({ r, levelled, showEntity }: {
  r: ResourceModel
  levelled: boolean
  showEntity: boolean
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2.5 px-[13px] pt-3">
      <div className="flex flex-wrap items-baseline gap-[7px]">
        <span className="font-sans text-[12.5px] font-semibold" style={{ color: 'var(--lc-ink)' }}>{r.name}</span>
        <span className={`${MONO} whitespace-nowrap`} style={{ color: 'var(--lc-mut)' }}>{r.sub}</span>
        {showEntity && (
          <span
            className="rounded-[3px] px-[5px] py-[2px] font-mono text-[8.5px] font-semibold uppercase tracking-[.07em]"
            style={{ background: 'var(--lc-tag-bg)', color: 'var(--lc-tag-ink)' }}
          >
            {r.site}
          </span>
        )}
      </div>
      <Badge r={r} levelled={levelled} />
    </div>
  )
}

function Column({ w, r, levelled, scale, on, onHover, onOpen }: {
  w: WeekModel
  r: ResourceModel
  levelled: boolean
  scale: number
  on: boolean | null
  onHover: (index: number | null) => void
  onOpen: (week: number) => void
}): React.JSX.Element {
  const segs = levelled ? levelledSegs(w, scale) : scheduledSegs(w, r.overdue, scale)
  return (
    <button
      type="button"
      aria-label={columnLabel(w, levelled)}
      className="relative flex-1 rounded-[3px]"
      style={{ opacity: on === false ? 0.6 : 1 }}
      onMouseEnter={() => onHover(w.index)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(w.index)}
      onBlur={() => onHover(null)}
      onClick={(e) => { e.stopPropagation(); onOpen(w.index) }}
    >
      <span className="flex flex-col-reverse justify-start overflow-hidden rounded-t-[2px]" style={{ height: PLOT }}>
        {segs.map((s) => <span key={s.key} data-seg={s.key} className={s.className} style={s.style} />)}
      </span>
      {on === true && <ColumnTooltip week={w} levelled={levelled} />}
    </button>
  )
}

function Plot({ r, levelled, hovered, onHover, onOpen }: {
  r: ResourceModel
  levelled: boolean
  hovered: number | null
  onHover: (index: number | null) => void
  onOpen: (week: number) => void
}): React.JSX.Element {
  const scale = r.capacity * (levelled ? 1.32 : 2.6)
  const ruleTop = 12 + PLOT * (1 - (scale ? r.capacity / scale : 0))
  return (
    <div className="relative px-[13px] pt-3">
      <div className="pointer-events-none absolute left-[13px] right-[13px] border-t border-dashed" style={{ top: ruleTop, borderColor: 'var(--lc-rule)' }} />
      <div
        className="absolute right-[13px] px-[3px] font-mono text-[8.5px] font-semibold tracking-[.05em]"
        style={{ top: ruleTop - 13, color: 'var(--lc-mut)', background: 'var(--lc-card)' }}
      >
        capacity {hrs(r.capacity)}/wk
      </div>
      <div className="flex items-end gap-[3px]" style={{ height: PLOT }}>
        {r.weeks.map((w) => (
          <Column
            key={w.weekStart}
            {...{ w, r, levelled, scale, onHover, onOpen }}
            on={hovered === null ? null : hovered === w.index}
          />
        ))}
      </div>
      {/* Thinned on a long horizon (format.tickEvery): the columns keep the card's width
          however many weeks arrive, and 52 day-numbers in that space is a grey wash. */}
      <div className="flex gap-[3px] pt-[5px]">
        {r.weeks.map((w, i) => (
          <div key={w.weekStart} className="flex-1 text-center font-mono text-[8.5px] font-medium" style={{ color: 'var(--lc-faint)' }}>
            {i % tickEvery(r.weeks.length) === 0 ? weekShort(w.weekStart) : '\u00a0'}
          </div>
        ))}
      </div>
    </div>
  )
}

function Footer({ r, onOpen }: { r: ResourceModel; onOpen: (week: number) => void }): React.JSX.Element {
  const n = r.weeks.length
  const tail = r.spare > 0 ? `${hrs(r.spare)} free over ${n}wk` : `${hrs(r.queuedAtEnd)} still queued at wk ${n}`
  return (
    <div className="mt-2 flex items-center justify-between gap-2.5 border-t px-[13px] pb-[11px] pt-[9px]" style={{ borderColor: 'var(--lc-b07)' }}>
      <span className={MONO} style={{ color: 'var(--lc-mut)' }}>{hrs(r.overdue)} overdue in · {tail}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen(0) }}
        className="whitespace-nowrap font-mono text-[10px] font-semibold tracking-[.06em]"
        style={{ color: 'var(--lc-mut)' }}
      >
        Open resource →
      </button>
    </div>
  )
}

export default function ResourceCard({ r, levelled, showEntity, hovered, onHover, onOpen }: {
  r: ResourceModel
  levelled: boolean
  showEntity: boolean
  hovered: number | null
  onHover: (index: number | null) => void
  onOpen: (week: number) => void
}): React.JSX.Element {
  return (
    <div
      className="lc-card cursor-pointer rounded-[10px] border"
      style={{ borderColor: 'var(--lc-b09)', background: 'var(--lc-card)' }}
      onClick={() => onOpen(0)}
    >
      <Header r={r} levelled={levelled} showEntity={showEntity} />
      <Plot r={r} levelled={levelled} hovered={hovered} onHover={onHover} onOpen={onOpen} />
      <Footer r={r} onOpen={onOpen} />
    </div>
  )
}
